/**
 * 점검 목록 페이지 (적응형 레이아웃)
 * 디바이스 유형에 따라 최적화된 UI를 제공합니다.
 */
import { useState, useCallback } from 'react';
import { useInspectionListPage } from '../../hooks/useInspectionListPage';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import {
  InspectionListDesktop,
  InspectionListFold,
  InspectionListPhone,
} from '../../components/inspection/layouts';
import CreateInspectionModal from '../../components/modals/CreateInspectionModal';
import SiteFormModal from '../../components/modals/SiteFormModal';
import { ReportTypeSelectModal } from '../../components/reports/ReportTypeSelectModal';
import { ConfirmDialog } from '../../components/inspection/shared/ConfirmDialog';
import { useAuthStore } from '../../stores/auth';
import { Button } from '@safetyhub/ui';
import { sitesApi } from '../../lib/organizations';

export default function InspectionsListAdaptive() {
  const layout = useDeviceLayout();
  const { currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;

  // 페이지네이션·정렬은 훅이 소유한다 — 둘 다 서버 재조회(offset/sort 파라미터)로 이어지므로
  // 조회 상태와 같은 자리에 있어야 한다.

  // 보고서 생성 모달 상태
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);
  const [selectedInspectionSiteId, setSelectedInspectionSiteId] = useState<string | null>(null);
  // (선택 점검의 기존 보고서 목록을 여기서 조회하지 않는다 — 양식별 중복 판정은 모달이
  //  templateKey로 직접 한다.)

  // 페이지 데이터 훅
  const pageData = useInspectionListPage();

  /**
   * 목록의 「보고서 생성」은 **양식 선택 모달을 띄운다.**
   *
   * ## ⚠️ 이 자리는 2026-10-08에 「상세로 이동」이었다 — 「원상태 복귀」가 아니다
   *
   * 그날의 결함: 목록 메뉴의 게이트가 **`COMPLETED` + 권한뿐**이라(`desktop/DropdownMenu.tsx`)
   * 상세의 정본 게이트(개선대책 **선택 1건 이상** + 참여자 전원 점검완료 서명)와 **답이 달랐다** —
   * 완료·서명까지 끝난 뒤 발견사항을 지워 선택이 0이 된 점검은 **상세에서는 막히고 목록에서는
   * 열렸다.** 그때 모달을 띄우는 것이 위험했던 이유는 **판정이 목록에 없었기** 때문이고, 그래서
   * 임시 처방은 「판정이 있는 곳(상세)으로 보낸다」였다.
   *
   * **지금은 그 전제가 바뀌었다.** 판정이 shared `resolveReportGate` 한 곳으로 올라가고 서버가
   * 그것을 `findAll` 응답의 `reportGate`에 실어 보낸다 ⇒ **목록 메뉴가 상세와 같은 답을 본다**
   * (`DropdownMenu`가 그 값을 **자기 권한과 AND**해서 판정한다). 그러므로 모달을 여는 것은
   * 사본을 하나 더 만드는 것이 아니다 — **게이트가 이미 메뉴를 비활성으로 만들었으므로 여기까지
   * 오는 클릭은 열린 점검뿐**이다. 사용자 결정(2026-10-09): 「평가 함수로 판정해 가능하면 모달,
   * 불가하면 메뉴 비활성」. 상세 한 번을 더 거치게 하지 않고, 중복이던 「상세 보기」 항목과도
   * 갈린다.
   *
   * ⚠️ 그래서 **되돌림의 방향이 바뀌었다**: 되돌리지 말아야 할 것은 「모달」이 아니라
   * **「메뉴가 `reportGate`를 읽는 것」**이다. 그 배선을 걷으면 모달이 다시 판정 없는 입구가 된다.
   * 래칫: `lib/__tests__/reportGate.test.ts`(⑦~⑫) · 이 파일의 짝 spec
   * `__tests__/index.adaptive.reportEntry.test.tsx`.
   */
  const handleGenerateReport = useCallback((id: string) => {
    // 선택한 점검에서 siteId 찾기
    const inspection = pageData.inspections.find(i => i.id === id);
    setSelectedInspectionId(id);
    setSelectedInspectionSiteId(inspection?.siteId || null);
    setShowReportModal(true);
  }, [pageData.inspections]);

  if (!companyId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  // 에러 표시
  if (pageData.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{pageData.error}</p>
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
          >
            새로고침
          </Button>
        </div>
      </div>
    );
  }

  // 공통 props
  const commonProps = {
    inspections: pageData.inspections,
    pagination: pageData.pagination,
    filters: pageData.filters,
    sites: pageData.sites,
    locations: pageData.locations,
    equipment: pageData.equipment,
    inProgressInspections: pageData.inProgressInspections,
    currentUserId: pageData.currentUserId,
    isOwner: pageData.isOwner,
    currentMembership,
    selectedSiteId: pageData.selectedSiteId,
    isLoading: pageData.isLoading,
    onFilterChange: pageData.onFilterChange,
    onViewInspection: pageData.onViewInspection,
    onDeleteInspection: pageData.onDeleteInspection,
    onRestoreInspection: pageData.onRestoreInspection,
    onRequestReview: pageData.onRequestReview,
    onStartScheduled: pageData.onStartScheduled,
    onStartNewClick: pageData.onStartNewClick,
    onStartNew: pageData.onStartNew,
    onContinueInspection: pageData.onContinueInspection,
    onToggleIncludeDeleted: pageData.onToggleIncludeDeleted,
  };

  // 레이아웃별 렌더링
  const renderLayout = () => {
    switch (layout) {
      case 'desktop':
        return (
          <InspectionListDesktop
            {...commonProps}
            onPageChange={pageData.onPageChange}
            onSort={pageData.onSort}
            onGenerateReport={handleGenerateReport}
            sortField={pageData.sortField}
            sortDirection={pageData.sortDirection}
          />
        );
      case 'fold':
        return (
          <InspectionListFold
            {...commonProps}
            onLoadMore={pageData.onLoadMore}
          />
        );
      case 'phone':
        return (
          <InspectionListPhone
            {...commonProps}
            onLoadMore={pageData.onLoadMore}
            onRefresh={pageData.onRefresh}
          />
        );
    }
  };

  return (
    <>
      {renderLayout()}

      {/* 점검 시작 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={pageData.startScheduledConfirmOpen}
        onClose={pageData.onCancelStartScheduled}
        onConfirm={pageData.onConfirmStartScheduled}
        title="점검 시작"
        message="이 점검을 시작하시겠습니까?"
        confirmText="시작"
        cancelText="취소"
        confirmStyle="primary"
      />

      {/* 점검 생성 모달 */}
      <CreateInspectionModal onSuccess={pageData.onRefresh} />

      {/* 현장 생성 모달 — source 기반으로 SiteFormModal 내부에서 inspection 재오픈 처리 */}
      <SiteFormModal
        onSuccess={() => {
          sitesApi.findAll(companyId!).then(() => {
            pageData.onRefresh();
          });
        }}
      />

      {/*
        문서 유형 선택 모달 — 입구의 판정은 행 메뉴가 **서버 `reportGate` ∧ 내 권한**으로 이미
        끝냈다(위 `handleGenerateReport` 주석). 이 모달이 하는 판정은 **양식별 중복**뿐이고
        (`templateKey` 기준) 생성 가능 여부 축과 겹치지 않는다.
      */}
      <ReportTypeSelectModal
        isOpen={showReportModal && !!selectedInspectionId}
        inspectionId={selectedInspectionId || ''}
        companyId={companyId || ''}
        siteId={selectedInspectionSiteId || ''}
        onClose={() => {
          setShowReportModal(false);
          setSelectedInspectionId(null);
          setSelectedInspectionSiteId(null);
        }}
      />
    </>
  );
}
